import {
  Grid,
  Typography,
  Box,
  Button,
  Popover,
  Stack,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import "../../assets/styles/common-styles.scss";
import AddIcon from "@mui/icons-material/Add";
import { DataGridComponent } from "../../components/common/DataGridComponent";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import AddStudyModal from "../../components/Studies/AddStudyModal";
import {
  setIsEditStudy,
  setOpenAddStudyModal,
  setOpenDeleteStudyDialog,
  setSelectedStudy,
  setStudyData,
} from "../../store/slices/studiesSlice";
import { useNavigate } from "react-router-dom";
import { useStudies } from "../../hooks/useStudies";

import DeleteStudyDialog from "../../components/Studies/DeleteStudyDialog";
import { useCodelist } from "../../hooks/useCodelist";
import { datagridUtils } from "../../utils/datagridUtils";
import studyListJson from "./studyListJson";

const Studies: React.FunctionComponent<any> = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [popoverDrugs, setPopoverDrugs] = useState([]);
  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    params: any
  ) => {
    setPopoverDrugs(params);
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const openDrug = Boolean(anchorEl);

  const codes = ["RECRUITMENT_STATUS"];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openAddStudyModal = useAppSelector(
    (state: AppState) => state.studiesSlice.openAddStudyModal
  );

  const openDeleteStudyDialog = useAppSelector(
    (state: AppState) => state.studiesSlice.openDeleteStudyDialog
  );

  const {
    rows,
    rowsCount,
    sortModel,
    setFilterObject,
    paginationModel,
    setPaginationModel,
    handleSortModelChange,
    allStudiesIsLoading,
    getStudyDetails,
  } = useStudies();

  const { getCodelistData, codelistData } = useCodelist();

  useEffect(() => {
    getCodelistData({ data: { code_list_codes: codes } });
    return () => {
      dispatch(setOpenAddStudyModal(false));
    };
  }, []);

  const studyStatusOptions = datagridUtils(
    codelistData,
    "RECRUITMENT_STATUS"
  )?.valueOptions;

  const studyListColumns = studyListJson(studyStatusOptions);

  const onEditClick = async (data: any) => {
    const response = await getStudyDetails({ study_id: data?.study_id });
    if (response?.data) {
      dispatch(setSelectedStudy(data?.study_id));
      dispatch(setStudyData(response?.data));
      dispatch(setOpenAddStudyModal(true));
      dispatch(setIsEditStudy(true));
    }
  };
  const onDeleteClick = (data: any) => {
    dispatch(setSelectedStudy(data?.study_id));
    dispatch(setOpenDeleteStudyDialog(true));
  };

  const actions = {
    study_id: (data: any) => {
      dispatch(setSelectedStudy(data?.study_id));
      navigate(`/studies/${encodeURIComponent(data?.study_id)}`);
    },
    actionsColumn: {
      action_edit: (data: any) => onEditClick(data),
      action_delete: (data: any) => onDeleteClick(data),
    },
  };

  const onAddStudyClick = () => {
    dispatch(setOpenAddStudyModal(true));
  };

  const treatmentView = (data: any) => {
    const popupDrugs = data.treatments && [...data.treatments];
    popupDrugs?.shift();
    return (
      <>
        <Grid container spacing={1}>
          <Grid
            item
            sx={{ maxWidth: "calc(100% - 60px)" }}
            className="textTruncate"
          >
            {data?.treatments[0]}
          </Grid>
          <Grid item>
            {data?.treatments.length > 1 && (
              <>
                <Chip
                  label={`+ ${data?.treatments.length - 1}`}
                  size="small"
                  variant="outlined"
                  aria-owns={openDrug ? "additional-drugs" : undefined}
                  aria-haspopup="true"
                  onMouseEnter={(e) => handlePopoverOpen(e, popupDrugs)}
                  onMouseLeave={handlePopoverClose}
                />
              </>
            )}
          </Grid>
        </Grid>
      </>
    );
  };
  const updatedRows = rows.map((item: any) => {
    return {
      ...item,
      treatments: treatmentView(item),
    };
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs>
          <Typography variant="h4" gutterBottom>
            Studies
          </Typography>
        </Grid>
        <Grid item justifyContent="flex-end">
          <Box display="flex" justifyContent="flex-end" columnGap={2}>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={onAddStudyClick}
              id="add_new_study"
            >
              Add New
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Box
        className="dataGridWrapBox"
        sx={{ width: "100%", height: "calc(100vh - 160px)" }}
      >
        <DataGridComponent
          columns={studyListColumns as any}
          hasDefinedColumns={false}
          rows={updatedRows}
          showQuickFilter
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={allStudiesIsLoading}
          rowsCount={rowsCount}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          checkboxSelection
          disableRowSelectionOnClick
          exportFileName="Study_List"
          actions={actions}
        />
      </Box>
      {openAddStudyModal && <AddStudyModal />}
      {openDeleteStudyDialog && <DeleteStudyDialog />}
      <Popover
        id="additional-drugs"
        sx={{
          pointerEvents: "none",
        }}
        open={openDrug}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Stack
          direction="column"
          rowGap={1}
          columnGap={1}
          p={2}
          alignItems="flex-start"
          className="textTruncate"
        >
          {popoverDrugs?.map((item: any) => (
            <Typography variant="body2" key={item} className="drugName">
              {item}
            </Typography>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default Studies;
